import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, CalendarDays } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { Faq } from "@/components/Faq";
import { PreventivoSection } from "@/components/PreventivoSection";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { AutoCorrelate } from "@/components/AutoCorrelate";
import { useSeo, faqSchema, SITE_URL } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dataIt = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "");

/** Guida storica (pagina statica già esistente) mostrata in testa all'elenco. */
const GUIDA_FISSA = {
  slug: null,
  to: "/guide/deducibilita-noleggio-auto-partita-iva/",
  titolo: "Deducibilità del noleggio auto per partita IVA: la guida completa",
  estratto: "Quanto si deduce, quanta IVA si detrae e come cambia tra professionisti, agenti di commercio e aziende.",
  tags: ["partita IVA", "fiscale"],
  copertina: "https://images.unsplash.com/photo-1477577314779-8dd53d8e615f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
};

export const BlogCard = ({ a }) => (
  <Link to={a.to || `/guide/${a.slug}/`} className="group flex flex-col rounded-2xl border border-xmove-ink/8 bg-white p-6 shadow-soft transition-transform hover:-translate-y-0.5" data-testid={`blog-card-${a.slug || "fissa"}`}>
    {a.copertina && <img src={a.copertina} alt="" className="mb-4 aspect-[16/9] w-full rounded-xl object-cover" loading="lazy" />}
    <div className="flex flex-wrap gap-1.5">{(a.tags || []).slice(0, 3).map((t) => <span key={t} className="rounded-full bg-xmove-cream px-2.5 py-0.5 text-[11px] font-semibold text-xmove-muted">{t}</span>)}</div>
    <h2 className="mt-3 font-display text-lg font-bold text-xmove-ink group-hover:text-xmove-orange">{a.titolo}</h2>
    <p className="mt-2 flex-1 text-sm text-xmove-muted">{a.estratto}</p>
    <span className="mt-4 flex items-center justify-between text-xs text-xmove-muted">
      {a.pubblicato_il && <span className="flex items-center gap-1"><CalendarDays size={12} /> {dataIt(a.pubblicato_il)}</span>}
      <span className="flex items-center gap-1 font-bold text-xmove-orange">Leggi <ArrowRight size={13} /></span>
    </span>
  </Link>
);

export function BlogIndex() {
  const [articoli, setArticoli] = useState(null);
  useEffect(() => { axios.get(`${API}/blog`).then((r) => setArticoli(r.data)).catch(() => setArticoli([])); }, []);
  useSeo({
    title: "Guide al noleggio lungo termine: nuovo, usato SecondLife, partita IVA | XMOVE Italia",
    description: "Il blog di XMOVE: guide pratiche su noleggio lungo termine, auto usate SecondLife (NLT Re-Use), deducibilità per partita IVA e aziende, consigli per scegliere l'auto giusta.",
    path: "/guide/",
    jsonLd: [{ "@context": "https://schema.org", "@type": "Blog", name: "Guide XMOVE Italia", url: `${SITE_URL}/guide/`, publisher: { "@type": "Organization", name: "XMOVE Italia", url: SITE_URL } }],
  });
  return (
    <div className="pb-[72px] md:pb-0" data-testid="blog-page">
      <Header />
      <section className="bg-xmove-night pb-14 pt-32 text-white lg:pb-20 lg:pt-40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-widest text-xmove-orange">Blog · Guide</p>
            <h1 className="mt-3 font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" data-testid="blog-h1">Guide al noleggio lungo termine, nuovo e usato.</h1>
            <p className="mt-5 max-w-2xl text-base text-white/70 md:text-lg">Articoli pratici, senza gergo: come funziona il noleggio, quando conviene l'usato SecondLife, cosa sapere se hai una partita IVA.</p>
          </Reveal>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8 lg:py-16">
        {articoli === null ? <p className="text-sm text-xmove-muted">Caricamento…</p> : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {[...articoli, GUIDA_FISSA].map((a) => <BlogCard key={a.slug || "fissa"} a={a} />)}
          </div>
        )}
      </section>
      <AutoCorrelate testid="blog-auto" />
      <PreventivoSection idPrefix="blog" title="Hai una domanda che non trovi nelle guide?" subtitle="Scrivici: un consulente XMOVE ti risponde senza impegno." />
      <Footer />
      <MobileStickyBar />
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [errore, setErrore] = useState(false);
  useEffect(() => {
    setA(null); setErrore(false);
    axios.get(`${API}/blog/${slug}`).then((r) => setA(r.data)).catch(() => setErrore(true));
  }, [slug]);
  const url = `${SITE_URL}/guide/${slug}/`;
  useSeo({
    title: a ? `${a.meta_title || a.titolo} | XMOVE Italia` : "Guida | XMOVE Italia",
    description: a ? a.meta_description || a.estratto || "" : "",
    path: `/guide/${slug}/`,
    image: a?.copertina,
    type: "article",
    noindex: errore,
    jsonLd: a ? [
      { "@context": "https://schema.org", "@type": "Article", headline: a.titolo, description: a.estratto, url, datePublished: a.pubblicato_il, dateModified: a.aggiornato_il, ...(a.copertina && { image: a.copertina }),
        author: { "@type": "Organization", name: "XMOVE Italia", url: SITE_URL }, publisher: { "@type": "Organization", name: "XMOVE Italia", url: SITE_URL }, mainEntityOfPage: url, keywords: (a.tags || []).join(", ") },
      { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` }, { "@type": "ListItem", position: 2, name: "Guide", item: `${SITE_URL}/guide/` }, { "@type": "ListItem", position: 3, name: a.titolo, item: url }] },
      ...(a.faqs?.length ? [faqSchema(a.faqs)] : []),
    ] : undefined,
  });

  if (errore) return (
    <div><Header /><div className="mx-auto max-w-3xl px-5 pb-24 pt-40 text-center" data-testid="blog-not-found">
      <h1 className="font-display text-3xl font-bold text-xmove-ink">Articolo non trovato</h1>
      <Link to="/guide/" className="mt-6 inline-block rounded-full bg-xmove-orange px-6 py-3 text-sm font-bold text-white">Tutte le guide</Link>
    </div><Footer /></div>
  );
  if (!a) return <div><Header /><div className="pt-40 text-center text-sm text-xmove-muted">Caricamento…</div></div>;

  return (
    <div className="pb-[72px] md:pb-0" data-testid="blog-post-page">
      <Header />
      <article className="mx-auto max-w-3xl px-5 pb-16 pt-32 lg:px-8 lg:pt-40">
        <Reveal>
          <nav aria-label="breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs text-xmove-muted">
            <Link to="/" className="hover:text-xmove-orange">Home</Link><span>/</span><Link to="/guide/" className="hover:text-xmove-orange">Guide</Link><span>/</span><span className="text-xmove-ink">{a.titolo}</span>
          </nav>
          <div className="mt-4 flex flex-wrap gap-1.5">{(a.tags || []).map((t) => <span key={t} className="rounded-full bg-xmove-cream px-2.5 py-0.5 text-[11px] font-semibold text-xmove-muted">{t}</span>)}</div>
          <h1 className="mt-3 font-display text-3xl font-bold leading-tight text-xmove-ink sm:text-4xl lg:text-5xl" data-testid="blog-post-h1">{a.titolo}</h1>
          {a.sottotitolo && <p className="mt-4 text-lg text-xmove-muted">{a.sottotitolo}</p>}
          <p className="mt-4 flex items-center gap-2 text-xs text-xmove-muted"><CalendarDays size={13} /> Pubblicato il {dataIt(a.pubblicato_il)}{a.aggiornato_il?.slice(0, 10) !== a.pubblicato_il?.slice(0, 10) && ` · aggiornato il ${dataIt(a.aggiornato_il)}`} · XMOVE Italia</p>
          {a.copertina && <img src={a.copertina} alt={a.titolo} className="mt-8 aspect-[16/9] w-full rounded-2xl object-cover" />}
          <div className="prose-xmove mt-10" data-testid="blog-post-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: ({ href, children }) => href?.startsWith("/") ? <Link to={href}>{children}</Link> : <a href={href} target="_blank" rel="noreferrer">{children}</a> }}>{a.contenuto}</ReactMarkdown>
          </div>
        </Reveal>
      </article>
      {a.faqs?.length > 0 && <Faq faqs={a.faqs} title="Domande frequenti" />}
      <AutoCorrelate testid="blog-post-auto" />
      <PreventivoSection idPrefix="blog" title="Vuoi una quotazione?" subtitle="Dicci cosa cerchi: ti rispondiamo con le soluzioni disponibili, nuove e SecondLife." />
      <Footer />
      <MobileStickyBar />
    </div>
  );
}
